import React, { useState } from 'react';
import axios from 'axios';
import { createApiUrl } from '../../utils/apiConfig';
import { restrictedVideoProps } from '../../utils/restrictedVideo';
import DragDropFileUpload from '../DragDropFileUpload';
import styles from './AssignedLessonsTab.module.css';

const AssignedLessonsTab = ({ 
  assignedLessons, 
  loadingAssignedLessons, 
  onUpdateStatus, 
  onRefresh 
}) => {
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [lessonResponse, setLessonResponse] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedFileForUpload, setSelectedFileForUpload] = useState(null);
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed':
        return 'badge bg-success';
      case 'in_progress':
        return 'badge bg-warning';
      case 'overdue':
        return 'badge bg-danger';
      default:
        return 'badge bg-secondary';
    }
  };

  const getStatusDisplayName = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
  };

  const handleStatusChange = (assignmentId, currentStatus) => {
    let newStatus;
    switch (currentStatus) {
      case 'assigned':
        newStatus = 'in_progress';
        break;
      case 'in_progress':
        newStatus = 'completed';
        break;
      default:
        return; // Don't allow changes from completed or overdue
    }
    onUpdateStatus(assignmentId, newStatus);
  };

  const _canChangeStatus = (status) => {
    return status === 'assigned' || status === 'in_progress';
  };

  const _getNextStatusButtonText = (status) => {
    switch (status) {
      case 'assigned':
        return 'Start Lesson';
      case 'in_progress':
        return 'Mark Complete';
      default:
        return '';
    }
  };

  const handleViewLesson = (assignment) => {
    setSelectedLesson(assignment);
    // Load existing submission if any
    setLessonResponse(assignment.submission?.response || '');
    setUploadedFiles(assignment.submission?.files || []);
    setShowLessonModal(true);
  };

  const closeLessonModal = () => {
    setShowLessonModal(false);
    setSelectedLesson(null);
    setLessonResponse('');
    setUploadedFiles([]);
    setSelectedFileForUpload(null);
  };

  const handleFileSelected = async (files) => {
    if (!files || !selectedLesson) return;

    // Handle both single file and array of files
    const fileArray = Array.isArray(files) ? files : [files];
    if (fileArray.length === 0) return;

    // Upload files one by one
    let successCount = 0;
    for (const file of fileArray) {
      const success = await uploadSingleFile(file);
      if (success) successCount++;
    }

    // Show summary message
    if (successCount > 0) {
      if (fileArray.length === 1) {
        alert('File uploaded successfully!');
      } else {
        alert(`${successCount} of ${fileArray.length} file(s) uploaded successfully!`);
      }
    }
  };

  const uploadSingleFile = async (file) => {
    if (!file || !selectedLesson) return false;

    setUploadingFile(true);
    setSelectedFileForUpload(file);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        createApiUrl(`/api/lesson-assignments/${selectedLesson._id}/upload-file`),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        }
      );

      if (response.data.success) {
        // Update the uploaded files list
        setUploadedFiles(prev => [...prev, response.data.data.file]);
        // Update selected lesson with new file
        setSelectedLesson(response.data.data.assignment);
        // Clear the selected file so drag-drop component resets
        setSelectedFileForUpload(null);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error uploading file:', error);
      setSelectedFileForUpload(null);
      return false;
    } finally {
      setUploadingFile(false);
    }
  };

  const handleFileRemove = () => {
    setSelectedFileForUpload(null);
  };

  const handleFileDelete = async (fileKey) => {
    if (!selectedLesson || !window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      // Encode the file key to handle special characters in S3 keys
      const encodedKey = encodeURIComponent(fileKey);
      const response = await axios.delete(
        createApiUrl(`/api/lesson-assignments/${selectedLesson._id}/files/${encodedKey}`),
        { withCredentials: true }
      );

      if (response.data.success) {
        // Remove file from list
        setUploadedFiles(prev => prev.filter(file => file.key !== fileKey));
        // Update selected lesson
        setSelectedLesson(response.data.data);
        alert('File deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Failed to delete file. Please try again.');
    }
  };

  const handleSaveProgress = async () => {
    if (!selectedLesson) return;
    
    setSaving(true);
    try {
      const response = await axios.post(
        createApiUrl(`/api/lesson-assignments/${selectedLesson._id}/save-progress`),
        { response: lessonResponse },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        // Update the selected lesson with the saved data
        setSelectedLesson(response.data.data);
        alert('Progress saved successfully!');
      }
    } catch (error) {
      console.error('Error saving progress:', error);
      alert('Failed to save progress. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitLesson = async () => {
    if (!selectedLesson) return;
    
    if (!lessonResponse.trim() && uploadedFiles.length === 0) {
      alert('Please write your response or upload a file before submitting.');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(
        createApiUrl(`/api/lesson-assignments/${selectedLesson._id}/submit`),
        { response: lessonResponse },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        // Update the selected lesson with the submitted data
        setSelectedLesson(response.data.data);
        // Refresh the lessons list
        if (onRefresh) {
          onRefresh();
        }
        alert('Lesson submitted successfully!');
        closeLessonModal();
      }
    } catch (error) {
      console.error('Error submitting lesson:', error);
      alert('Failed to submit lesson. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.assignedLessonsTab}>
      <div className={styles.header}>
        <h4>My Assigned Lessons</h4>
        <button 
          className="btn btn-outline-primary btn-sm"
          onClick={onRefresh}
          disabled={loadingAssignedLessons}
        >
          {loadingAssignedLessons ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {loadingAssignedLessons ? (
        <div className={styles.loading}>
          <p>Loading assigned lessons...</p>
        </div>
      ) : assignedLessons.length === 0 ? (
        <div className={styles.empty}>
          <p>No lessons have been assigned to you yet.</p>
          <p className="text-muted">Your teacher will assign lessons that will appear here.</p>
        </div>
      ) : (
        <div className={styles.lessonsList}>
          {assignedLessons.map((assignment) => (
            <div key={assignment._id} className={styles.lessonCard}>
              <div className={styles.lessonHeader}>
                <h5 className={styles.lessonTitle}>
                  {assignment.lessonId?.name || ''}
                </h5>
                <span className={getStatusBadgeClass(assignment.status)}>
                  {getStatusDisplayName(assignment.status)}
                </span>
              </div>
              
              {assignment.lessonId?.description && (
                <p className={styles.lessonDescription}>
                  {assignment.lessonId.description}
                </p>
              )}

              <div className={styles.lessonMeta}>
                <div className={styles.metaItem}>
                  <strong>Assigned by:</strong> {assignment.teacherId?.contactName || ''}
                </div>
                <div className={styles.metaItem}>
                  <strong>Assigned on:</strong> {new Date(assignment.assignedDate).toLocaleDateString()}
                </div>
                {assignment.dueDate && (
                  <div className={styles.metaItem}>
                    <strong>Due:</strong> {new Date(assignment.dueDate).toLocaleDateString()}
                  </div>
                )}
                {assignment.completedDate && (
                  <div className={styles.metaItem}>
                    <strong>Completed:</strong> {new Date(assignment.completedDate).toLocaleDateString()}
                  </div>
                )}
              </div>

              <div className={styles.lessonActions}>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => handleViewLesson(assignment)}
                >
                  {assignment.status === 'completed' ? 'View Lesson' : 'View & Work on Lesson'}
                </button>
                {assignment.status === 'assigned' && (
                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => {
                      handleStatusChange(assignment._id, assignment.status);
                      handleViewLesson({ ...assignment, status: 'in_progress' });
                    }}
                  >
                    Start Lesson
                  </button>
                )}
              </div>

              {assignment.notes && (
                <div className={styles.notes}>
                  <strong>Teacher Notes:</strong>
                  <p>{assignment.notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Lesson Viewer Modal */}
      {showLessonModal && selectedLesson && (
        <div className={styles.lessonModalOverlay} onClick={closeLessonModal}>
          <div className={styles.lessonModalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.lessonModalHeader}>
              <h3>{selectedLesson.lessonId?.name || 'Lesson'}</h3>
              <button className={styles.closeButton} onClick={closeLessonModal}>×</button>
            </div>
            
            <div className={styles.lessonModalBody}>
              {selectedLesson.lessonId?.description && (
                <div className={styles.lessonDescription}>
                  <h5>Description</h5>
                  <p>{selectedLesson.lessonId.description}</p>
                </div>
              )}

              {/* Video Content */}
              {selectedLesson.lessonId?.video && (
                <div className={styles.lessonContent}>
                  <h5>Lesson Video</h5>
                  <div className={styles.videoViewer}>
                    <video
                      controls
                      {...restrictedVideoProps}
                      width="100%"
                      style={{ borderRadius: '8px' }}
                    >
                      <source 
                        src={typeof selectedLesson.lessonId.video === 'string' 
                          ? selectedLesson.lessonId.video 
                          : selectedLesson.lessonId.video?.url} 
                        type="video/mp4" 
                      />
                      <source 
                        src={typeof selectedLesson.lessonId.video === 'string' 
                          ? selectedLesson.lessonId.video 
                          : selectedLesson.lessonId.video?.url} 
                        type="video/webm" 
                      />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                </div>
              )}

              {/* File Content */}
              {selectedLesson.lessonId?.fileUrl && (
                <div className={styles.lessonContent}>
                  <h5>Lesson Content</h5>
                  <div className={styles.fileViewer}>
                    {selectedLesson.lessonId.fileUrl.includes('.pdf') ? (
                      <iframe
                        src={selectedLesson.lessonId.fileUrl}
                        width="100%"
                        height="500px"
                        style={{ border: 'none', borderRadius: '8px' }}
                        title="Lesson PDF"
                      />
                    ) : selectedLesson.lessonId.fileUrl.includes('video') || selectedLesson.lessonId.fileUrl.includes('.mp4') || selectedLesson.lessonId.fileUrl.includes('.webm') ? (
                      <video
                        controls
                        {...restrictedVideoProps}
                        width="100%"
                        style={{ borderRadius: '8px' }}
                      >
                        <source src={selectedLesson.lessonId.fileUrl} type="video/mp4" />
                        <source src={selectedLesson.lessonId.fileUrl} type="video/webm" />
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <div className={styles.fileLink}>
                        <a 
                          href={selectedLesson.lessonId.fileUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn btn-primary"
                        >
                          Open Lesson File
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedLesson.notes && (
                <div className={styles.teacherNotes}>
                  <h5>Teacher Notes</h5>
                  <p>{selectedLesson.notes}</p>
                </div>
              )}

              {/* Student Response Section */}
              <div className={styles.lessonContent}>
                <h5>Your Response</h5>
                <div className="mb-3">
                  <label className="form-label">
                    <strong>Write your response here:</strong>
                    <small className="text-muted d-block">
                      {selectedLesson.status === 'completed' 
                        ? 'Your submitted response (you can view but not edit)' 
                        : 'Write your essay, answer, or response to the lesson here. You can save your progress and submit when ready.'}
                    </small>
                  </label>
                  <textarea
                    className="form-control"
                    placeholder="Write your response to the lesson here. For example, if the lesson asks you to write an essay after watching a video, write your essay here..."
                    value={lessonResponse}
                    onChange={(e) => setLessonResponse(e.target.value)}
                    rows={10}
                    style={{ minHeight: '200px' }}
                    disabled={selectedLesson.status === 'completed'}
                  />
                </div>

                {/* File Upload Section */}
                <div className="mb-3">
                  <label className="form-label">
                    <strong>Upload Files (Optional):</strong>
                    <small className="text-muted d-block">
                      You can upload Word documents, PDFs, images, or other files as part of your response. Click to browse or drag and drop files here.
                    </small>
                  </label>
                  {selectedLesson.status !== 'completed' && (
                    <div className="mb-2">
                      <DragDropFileUpload
                        onFileSelected={handleFileSelected}
                        onFileRemoved={handleFileRemove}
                        selectedFile={selectedFileForUpload}
                        accept=".doc,.docx,.pdf,.txt,.jpg,.jpeg,.png,.gif,.ppt,.pptx"
                        multiple={true}
                        maxFileSize={50 * 1024 * 1024} // 50MB
                        supportedTypes={['.doc', '.docx', '.pdf', '.txt', '.jpg', '.jpeg', '.png', '.gif', '.ppt', '.pptx']}
                        placeholder="Click to browse or drag & drop files here"
                        description="Supports: Word, PDF, Images, PowerPoint, Text files (up to 50MB)"
                        disabled={uploadingFile}
                        showFileInfo={true}
                      />
                      {uploadingFile && (
                        <div className="mt-2">
                          <small className="text-info">
                            <i className="fas fa-spinner fa-spin me-1"></i>
                            Uploading file...
                          </small>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Display Uploaded Files */}
                  {uploadedFiles.length > 0 && (
                    <div className="mt-3">
                      <strong>Uploaded Files:</strong>
                      <ul className="list-group mt-2">
                        {uploadedFiles.map((file, index) => (
                          <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                            <div>
                              <a 
                                href={file.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-decoration-none"
                              >
                                <i className="fas fa-file me-2"></i>
                                {file.name}
                              </a>
                              <small className="text-muted d-block">
                                Uploaded: {new Date(file.uploadedAt).toLocaleString()}
                              </small>
                            </div>
                            {selectedLesson.status !== 'completed' && (
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleFileDelete(file.key)}
                                title="Delete file"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {selectedLesson.submission?.lastSaved && (
                  <div className="text-muted small mb-2">
                    <i className="fas fa-save me-1"></i>
                    Last saved: {new Date(selectedLesson.submission.lastSaved).toLocaleString()}
                  </div>
                )}
                {selectedLesson.submission?.submittedAt && (
                  <div className="text-success small mb-2">
                    <i className="fas fa-check-circle me-1"></i>
                    Submitted: {new Date(selectedLesson.submission.submittedAt).toLocaleString()}
                  </div>
                )}
              </div>

              <div className={styles.lessonMeta}>
                <div className={styles.metaRow}>
                  <strong>Assigned by:</strong> {selectedLesson.teacherId?.contactName || ''}
                </div>
                <div className={styles.metaRow}>
                  <strong>Assigned on:</strong> {new Date(selectedLesson.assignedDate).toLocaleDateString()}
                </div>
                {selectedLesson.dueDate && (
                  <div className={styles.metaRow}>
                    <strong>Due:</strong> {new Date(selectedLesson.dueDate).toLocaleDateString()}
                  </div>
                )}
                <div className={styles.metaRow}>
                  <strong>Status:</strong> 
                  <span className={getStatusBadgeClass(selectedLesson.status)}>
                    {getStatusDisplayName(selectedLesson.status)}
                  </span>
                </div>
              </div>
            </div>

            <div className={styles.lessonModalFooter}>
              {selectedLesson.status !== 'completed' && (
                <>
                  <button 
                    className="btn btn-info"
                    onClick={handleSaveProgress}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Progress'}
                  </button>
                  <button 
                    className="btn btn-success"
                    onClick={handleSubmitLesson}
                    disabled={submitting || (!lessonResponse.trim() && uploadedFiles.length === 0)}
                  >
                    {submitting ? 'Submitting...' : 'Submit Lesson'}
                  </button>
                </>
              )}
              <button className="btn btn-secondary" onClick={closeLessonModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignedLessonsTab;
